import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { I18NextModule } from 'angular-i18next';
import i18next from 'i18next';
import { CoreService } from 'src/app/core/services/core.service';
import { UserService } from 'src/app/core/services/user.service';
import { FeedbackModalComponent } from 'src/app/shared/components/feedback-modal/feedback-modal.component';
import { User } from 'src/app/shared/model/user.model';
import {
  ExternalCompanyActionResponse,
  ExternalCompanyEmployee,
  ExternalCompanyEmployeesResponse
} from 'src/app/shared/model/external-company-employee.model';

@Component({
  standalone: true,
  selector: 'app-extern-vo',
  templateUrl: './extern-vo.component.html',
  styleUrls: ['./extern-vo.component.scss'],
  imports: [CommonModule, MatButtonModule, MatPaginatorModule, I18NextModule]
})
// CRMSP-7485 - Empleados VO
export class ExternVoComponent implements OnInit {
  readonly pageSize = 10;
  pendingEmployees: ExternalCompanyEmployee[] = [];
  activeEmployees: ExternalCompanyEmployee[] = [];
  pendingPageIndex = 0;
  activePageIndex = 0;
  spinner = false;

  constructor(
    private coreService: CoreService,
    private userService: UserService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadEmployees();
  }

  loadEmployees(): void {
    this.spinner = true;

    this.userService.getUser().subscribe({
      next: (user: User) => {
        this.coreService.getExternalUsers(user.nis).subscribe({
          next: (response: ExternalCompanyEmployeesResponse) => {
            const employees = response.employees ?? [];
            this.pendingEmployees = employees.filter(
              (employee: ExternalCompanyEmployee) =>
                employee.status.toUpperCase() === 'PENDING'
            );
            this.activeEmployees = employees.filter(
              (employee: ExternalCompanyEmployee) =>
                (employee.status ?? '').toUpperCase() === 'ACTIVE'
            );
            this.pendingPageIndex = 0;
            this.activePageIndex = 0;
            this.spinner = false;
          },
          error: () => {
            this.pendingEmployees = [];
            this.activeEmployees = [];
            this.showError('externalVoLoadError');
          }
        });
      },
      error: () => {
        this.pendingEmployees = [];
        this.activeEmployees = [];
        this.showError('externalVoLoadError');
      }
    });
  }

  get paginatedPendingEmployees(): ExternalCompanyEmployee[] {
    const start = this.pendingPageIndex * this.pageSize;
    return this.pendingEmployees.slice(start, start + this.pageSize);
  }

  get paginatedActiveEmployees(): ExternalCompanyEmployee[] {
    const start = this.activePageIndex * this.pageSize;
    return this.activeEmployees.slice(start, start + this.pageSize);
  }

  changePendingPage(event: PageEvent): void {
    this.pendingPageIndex = event.pageIndex;
  }

  changeActivePage(event: PageEvent): void {
    this.activePageIndex = event.pageIndex;
  }

  approve(employee: ExternalCompanyEmployee): void {
    this.spinner = true;
    this.coreService.approveExternalUser(employee.id).subscribe({
      next: (response: ExternalCompanyActionResponse) => {
        if (response.success) {
          this.loadEmployees();
        } else {
          this.showError('externalVoApproveError');
        }
      },
      error: () => {
        this.showError('externalVoApproveError');
      }
    });
  }

  reject(employee: ExternalCompanyEmployee): void {
    this.spinner = true;
    this.coreService.rejectExternalUser(employee.id).subscribe({
      next: (response: ExternalCompanyActionResponse) => {
        if (response.success) {
          this.loadEmployees();
        } else {
          this.showError('externalVoRejectError');
        }
      },
      error: () => {
        this.showError('externalVoRejectError');
      }
    });
  }

  deactivate(employee: ExternalCompanyEmployee): void {
    this.spinner = true;
    this.coreService.deactivateExternalUser(employee.id).subscribe({
      next: (response: ExternalCompanyActionResponse) => {
        if (response.success) {
          this.loadEmployees();
        } else {
          this.showError('externalVoDeactivateError');
        }
      },
      error: () => {
        this.showError('externalVoDeactivateError');
      }
    });
  }

  private showError(messageKey: string): void {
    this.spinner = false;
    this.dialog.open(FeedbackModalComponent, {
      width: '400px',
      data: {
        text: i18next.t(messageKey),
        close: true,
        theme: 'light'
      }
    });
  }

  trackById(_: number, employee: ExternalCompanyEmployee): string {
    return employee.id;
  }
}
